import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Textfield from 'syf-component-library/ui/atoms/Textfield';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE } from '../../../../const/analytics';
import { FAL_EXCLAMATION_CIRCLE, FAR_ANGLE_DOWN, FAR_ANGLE_UP, FAR_DOLLAR_SIGN } from '../../../../const/iconProps';
import { paymentOptions } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { getLoanProductPaymentAmount, isAutopayPaymentAmountOptionMutable, isInstallmentLoan, isMonthly, isPN4, isSecuredInstallmentLoanProduct, isSetpayLegacyProduct, isWaivedInterestAvailableForAutopay } from '../../../../helpers';
import getPaymentAmountFromOption from '../../../../helpers/getPaymentAmountFromOption';
import isDCHCustomerAssistancePlanEnrolled from '../../../../helpers/isDCHCustomerAssistancePlanEnrolled';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import { PAYMENT_AMOUNT_INPUT_NAME } from '../../../../machines/autopay/paymentAmountConfig';
import { PAYMENT_OPTION_INPUT_NAME } from '../../../../machines/autopay/paymentOptionConfig';
import { t } from '../../../../strings/i18n';
import SelectWithFocus from '../../../../ui/atoms/SelectWithFocus';
import { useField } from '../../../../xform';
import { OtherOption, SelectWrapper } from '../subcomponents';
import { createPaymentAmountOption, getAutopayPaymentAmountHelpText, getOtherAmountHelpText } from './helpers';
const getAmountOptions = (apiContext) => {
    const accountSummary = apiContext.accountSummaryQueryResult.data;
    const { billing } = accountSummary;
    const { featureFlags } = apiContext;
    const enterpriseEligibility = apiContext.paymentEnterpriseEligibilityQueryResult.data;
    const productType = accountSummary.product.type;
    const otherAmountOption = {
        element: (_jsx(OtherOption, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "other-amount-option", "data-object": DATA_OBJECT.BUTTON, "data-reason": DATA_REASON.PAYMENT_AMOUNT.OTHER_AMOUNT, "data-type": DATA_TYPE.MAKE_A_PAYMENT }, { children: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`) }))),
        value: paymentOptions.OTHER_PAYMENT,
        label: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`)
    };
    if (isMonthly(productType)) {
        const monthlyFixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.MONTHLY_FIXED_PAYMENT_AMOUNT, apiContext);
        if (isWaivedInterestAvailableForAutopay(enterpriseEligibility)) {
            const waivedInterestAutopayAmountOption = createPaymentAmountOption(paymentOptions.WAIVED_INTEREST_AUTOPAY_AMOUNT, apiContext);
            return [
                monthlyFixedPaymentAmountOption,
                waivedInterestAutopayAmountOption
            ];
        }
        return [monthlyFixedPaymentAmountOption];
    }
    if (isPN4(productType)) {
        const pn4FixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.PN4_FIXED_PAYMENT_AMOUNT, apiContext);
        return [pn4FixedPaymentAmountOption];
    }
    if (isSetpayLegacyProduct(productType)) {
        const monthlyPaymentOption = createPaymentAmountOption(paymentOptions.MONTHLY_PAYMENT, apiContext);
        return [monthlyPaymentOption, otherAmountOption];
    }
    if (isSecuredInstallmentLoanProduct(productType)) {
        const securedInstallmentFixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.SECURED_INSTALLMENT_FIXED_PAYMENT_AMOUNT, apiContext);
        return [securedInstallmentFixedPaymentAmountOption];
    }
    if (isDCHCustomerAssistancePlanEnrolled(billing, productType, featureFlags)) {
        const revolvingCreditFixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.NEW_MINIMUM_MONTHLY_DUE, apiContext);
        return [revolvingCreditFixedPaymentAmountOption];
    }
    const statementBalanceOption = createPaymentAmountOption(paymentOptions.STATEMENT_BALANCE, apiContext);
    const totalMinimumPaymentOption = createPaymentAmountOption(paymentOptions.MINIMUM_PAYMENT_DUE, apiContext);
    return [statementBalanceOption, totalMinimumPaymentOption, otherAmountOption];
};
const getDefaultOptions = (amountOptions, paymentOption) => {
    return amountOptions.find(({ value }) => value === paymentOption);
};
const AutopayAmountSelect = ({ id, disabled = false }) => {
    const [apiMachine] = useApiMachine();
    const { featureFlags } = useContext(MainContext);
    const { data: accountSummary } = apiMachine.context.accountSummaryQueryResult;
    const { data: paymentEnterpriseEligibility } = apiMachine.context.paymentEnterpriseEligibilityQueryResult;
    const { billing } = accountSummary;
    const productType = accountSummary.product.type;
    const amount = isInstallmentLoan(productType)
        ? getLoanProductPaymentAmount(accountSummary)
        : billing.totalMinimumPaymentDue;
    const paymentOptionField = useField(PAYMENT_OPTION_INPUT_NAME);
    const paymentAmountField = useField(PAYMENT_AMOUNT_INPUT_NAME);
    const otherAmountKey = `${id}Other`;
    const isOtherAmount = paymentOptionField.value === paymentOptions.OTHER_PAYMENT;
    const amountOptions = getAmountOptions(apiMachine.context);
    const rightIconClosed = isAutopayPaymentAmountOptionMutable(paymentEnterpriseEligibility, productType, featureFlags, billing) ? (_jsx(FontAwesomeIcon, { icon: FAR_ANGLE_DOWN, size: "lg" })) : null;
    const paymentAmount = isOtherAmount
        ? paymentAmountField.value
        : getPaymentAmountFromOption(paymentOptionField.value, apiMachine.context);
    const [otherAmountHelpText, setOtherAmountHelpText] = useState(getOtherAmountHelpText(paymentAmountField.value, amount));
    const paymentOptionChangeHandler = ({ value }) => {
        paymentOptionField.setValue(value);
        paymentOptionField.blur();
        if (value !== paymentOptions.OTHER_PAYMENT) {
            paymentAmountField.reset();
        }
    };
    const paymentAmountChangeHandler = ({ target }) => {
        paymentAmountField.setValue(target.value);
        setOtherAmountHelpText('');
    };
    const paymentAmountBlurHandler = () => {
        paymentAmountField.blur();
        setOtherAmountHelpText(getOtherAmountHelpText(paymentAmountField.value, amount));
    };
    const autopayPaymentAmountHelpText = getAutopayPaymentAmountHelpText(productType, paymentEnterpriseEligibility, billing.fixedPaymentAmount, paymentOptionField.value);
    return (_jsxs(_Fragment, { children: [_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_OPTION_INPUT_NAME }, { children: _jsx(SelectWithFocus, { autoAdjust: true, dataCy: "payment-amount-field", defaultOption: getDefaultOptions(amountOptions, paymentOptionField.value), error: paymentOptionField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAL_EXCLAMATION_CIRCLE, size: "1x" }), helpText: autopayPaymentAmountHelpText, id: id, isDisabled: paymentOptionField.isDisabled || disabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), name: id, options: amountOptions, placeholder: t('label.paymentAmountPlaceholder'), required: true, rightIconClosed: rightIconClosed, rightIconOpen: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_UP, size: "lg" }), tabIndex: paymentOptionField.isDisabled ? -1 : 0, onChange: paymentOptionChangeHandler }) })), isOtherAmount && (_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_AMOUNT_INPUT_NAME }, { children: _jsx(Textfield, { autoCapitalize: "none", autoComplete: "transaction-amount", dataCy: "other-amount-text-field", error: paymentAmountField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAL_EXCLAMATION_CIRCLE, size: "1x" }), helpText: otherAmountHelpText, id: otherAmountKey, inputMode: "decimal", isDisabled: paymentAmountField.isDisabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), matchPattern: /^[0-9]*(\.[0-9]{0,2})?$/, name: otherAmountKey, placeholder: t('text.otherAmountOption'), required: true, type: "text", value: paymentAmount, width: "100%", onBlur: paymentAmountBlurHandler, onChange: paymentAmountChangeHandler }) })))] }));
};
export default AutopayAmountSelect;
